import React from 'react';

import {
  Button,
  Checkbox,
  Group,
  Input,
  LoadingOverlay,
  Stack,
  TextInput,
  Title,
  px,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import PostalCodeInput from 'components/form-fields/input-fields/PostalCodeInput';
import usePaymentMethod from 'hooks/paymentMethod/usePaymentMethod';
import useUpdatePaymentMethod from 'hooks/paymentMethod/useUpdatePaymentMethod';
import InputMask from 'react-input-mask';
import { isValidCountry } from 'types/enums';
import { getPaymentMethodName } from 'utils/formatters';
import notificationHandler from 'utils/notifications';
import { postalCodeValidator } from 'utils/payment.helpers';

interface FormValues {
  paymentMethodId: string;
  nickname: string;
  isDefault: boolean;
  postalCode: string | null;
  expirationDate: string | null;
}

interface IEditPaymentMethodDrawerProps {
  closeHandler: () => void;
  opened: boolean;
  paymentMethodId: string;
}

const EditPaymentMethodForm = ({
  closeHandler,
  opened,
  paymentMethodId,
}: IEditPaymentMethodDrawerProps): JSX.Element => {
  const { data, isSuccess, isLoading } = usePaymentMethod(paymentMethodId);
  const theme = useMantineTheme();
  const updateMutation = useUpdatePaymentMethod();
  const { errorNotification, successNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      paymentMethodId: paymentMethodId,
      nickname: '',
      isDefault: false,
      postalCode: null,
      expirationDate: null,
    },

    validate: {
      nickname: value => (value.length > 0 ? null : 'Please enter a name'),
      postalCode: code => {
        const country = data?.data.metadata?.country;
        const isCardType = data?.data.type === 'CARD';
        const isCountryValid = isValidCountry(country);
        const isPostalCodeValidationOn = isCardType && isCountryValid;

        return isPostalCodeValidationOn ? postalCodeValidator(code, country) : null;
      },
      expirationDate: value =>
        data?.data.type === 'CARD' && value?.replace('_', '').length != 5
          ? 'Please enter an expiration date'
          : null,
    },
    transformValues: (values: FormValues): FormValues => ({
      ...values,
      postalCode: values.postalCode?.toUpperCase() || null,
    }),
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleSubmit = (values: FormValues): void => {
    updateMutation.mutate(values, {
      onError: error => {
        errorNotification('Payment method update failed', error.response?.data.message);
      },
      onSuccess: response => {
        successNotification(
          'Payment method update success',
          `${response.data.nickname} successfully updated.`,
        );
        handleCloseAndReset();
      },
    });
  };

  useShallowEffect(() => {
    if (data?.data && isSuccess) {
      form.setValues({
        paymentMethodId: data.data.paymentMethodId,
        nickname: data.data.nickname || getPaymentMethodName(data.data),
        isDefault: data.data.isDefault,
        postalCode:
          data.data.type === 'CARD' ? data?.data.metadata?.billingDetails?.address?.postalCode : null,
        expirationDate:
          data.data.type === 'CARD'
            ? `${(data?.data.metadata?.expMonth || 1)
                .toString()
                .padStart(2, '0')}/${data?.data.metadata?.expYear.toString().slice(-2)}`
            : null,
      });
    }
  }, [data?.data]);

  return (
    <Drawer
      closeOnClickOutside
      opened={opened}
      onClose={handleCloseAndReset}
      size='lg'
      title={<Title order={3}>Edit Payment Method</Title>}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='edit-payment-method-form'
            loading={updateMutation.isLoading || isLoading}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={isLoading} />
      <form id='edit-payment-method-form' onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='xs'>
          <TextInput {...form.getInputProps('nickname')} label='Name' placeholder='Enter nick name' />
          <Checkbox
            {...form.getInputProps('isDefault', { type: 'checkbox' })}
            disabled={data?.data.isDefault}
            label='Set as default payment method'
            description={
              data?.data.isDefault
                ? 'Cannot unset without choosing a new default'
                : 'Will be used for future invoices'
            }
          />
          {data?.data.type === 'CARD' && (
            <Input.Wrapper>
              <Input.Label>Expiration Date</Input.Label>
              <Input
                component={InputMask}
                placeholder='MM/YY'
                mask='99/99'
                {...form.getInputProps('expirationDate')}
                mb={px(theme.spacing.xs) / 2}
              />
              <Input.Error>{form.errors.expirationDate}</Input.Error>
            </Input.Wrapper>
          )}
          {data?.data.type === 'CARD' && isValidCountry(data?.data.metadata?.country) && (
            <PostalCodeInput
              country={data?.data.metadata?.country}
              {...form.getInputProps('postalCode')}
              mb={px(theme.spacing.xs) / 2}
              formErrors={form.errors.postalCode}
            />
          )}
        </Stack>
      </form>
    </Drawer>
  );
};

export default EditPaymentMethodForm;
